import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  usePrimaryComplaintStore,
  type ProblemOat,
} from "@/store/primaryComplaint";
import { getFacilityKey } from "@/store/profile";
import { useTaskStore } from "@/store/task";
import { complaintHistoryLabels as t } from "@/components/complaints/complaintHistoryLabels";
import { Button } from "@/components/ui/Button";
import { WizardActionsBar } from "@/components/wizard/WizardActionsBar";
import { cn } from "@/utils/cn";
import { ComplaintStepHeader, StepLoadError } from "@/components/complaints/ComplaintStepHeader";
import { DEDICATED_REASON_ROUTES, resolveComplaintType } from "@/utils/complaintSteps";

/**
 * Outcome assessment for the current complaint. Questions are loaded from the API
 * per complaint. One question at a time with progress; answers stacked vertically.
 * Back → previous question, then the complaint's previous step; Continue → complaint-summary.
 */
export function OutcomeAssessmentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const complaintId = searchParams.get("complaintId");
  const patientID = searchParams.get("patientID");
  const caseHistoryIdParam = searchParams.get("caseHistoryId");
  const taskCaseHistoryId = useTaskStore((s) => s.caseHistory?.id);
  const caseHistoryId = caseHistoryIdParam ?? (taskCaseHistoryId ? String(taskCaseHistoryId) : null);

  const problemOat = usePrimaryComplaintStore((s) => s.problemOat);
  const fetchProblemOat = usePrimaryComplaintStore((s) => s.fetchProblemOat);
  const fetchCaseHistoryOats = usePrimaryComplaintStore(
    (s) => s.fetchCaseHistoryOats
  );
  const updateProblemOat = usePrimaryComplaintStore((s) => s.updateProblemOat);
  const setProblemOat = usePrimaryComplaintStore((s) => s.setProblemOat);
  const complaintHeaders = usePrimaryComplaintStore((s) => s.complaintHeaders);

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [savingIndex, setSavingIndex] = useState<number | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  // Next with no answer explains itself inline (a disabled button's title tooltip never shows on touch).
  const [missing, setMissing] = useState(false);
  useEffect(() => setMissing(false), [currentIndex]);

  const search =
    caseHistoryId
      ? `?complaintId=${complaintId}&caseHistoryId=${caseHistoryId}`
      : `?complaintId=${complaintId}&patientID=${patientID ?? ""}`;

  const loadOats = useCallback(async () => {
    if (!complaintId || (!patientID && !caseHistoryId)) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setLoadError(false);
    try {
      if (caseHistoryId) {
        await fetchCaseHistoryOats(getFacilityKey(), caseHistoryId, complaintId);
      } else {
        await fetchProblemOat(complaintId);
      }
    } catch {
      // Distinguish a failed load from a genuinely empty questionnaire — don't silently skip it.
      setLoadError(true);
    } finally {
      setIsLoading(false);
    }
  }, [complaintId, patientID, caseHistoryId, fetchProblemOat, fetchCaseHistoryOats]);

  useEffect(() => {
    void loadOats();
  }, [loadOats]);

  // If there are genuinely no questions for this complaint, skip to "any other complaints?".
  // A failed load (loadError) must NOT skip — the patient would lose the whole questionnaire silently.
  useEffect(() => {
    if (isLoading || loadError) return;
    if (!complaintId || (!patientID && !caseHistoryId)) return;
    const questions = problemOat?.questions ?? [];
    if (questions.length > 0) return;
    navigate({ pathname: "/complaint/complaint-summary", search }, { replace: true });
  }, [isLoading, loadError, problemOat, complaintId, patientID, caseHistoryId, navigate, search]);

  const handleAnswer = (oat: ProblemOat, index: number, answer: string) => {
    if (!complaintId) return;
    const updated: ProblemOat = {
      ...oat,
      questions: oat.questions.map((q, i) =>
        i === index ? { ...q, answer } : q
      ),
    };
    setProblemOat(updated);
    setSavingIndex(index);
    const totalQuestions = oat.questions.length;
    updateProblemOat(complaintId, updated, index)
      .then(() => {
        // Advance only once the answer is actually saved — never move past a lost answer.
        if (index === totalQuestions - 1) saveAndContinue();
        else setCurrentIndex((i) => Math.min(totalQuestions - 1, i + 1));
      })
      .catch(() => {
        // Failure is surfaced via toast; stay on this question so the patient can retry.
      })
      .finally(() => setSavingIndex(null));
  };

  // Headaches / dizziness / wellness came from their own screen, not the body-flow factors step.
  // Only this complaint's own header decides: the last reason picked may belong to another complaint,
  // and its screen shares field codes, so guessing could write one form's answers over another's.
  const complaintType = resolveComplaintType(complaintHeaders, complaintId, null);
  const previousStep =
    (complaintType && DEDICATED_REASON_ROUTES[complaintType as keyof typeof DEDICATED_REASON_ROUTES]) ||
    "/complaint/complaint-factors";
  const goBackWizard = () => navigate({ pathname: previousStep, search });

  const saveAndContinue = () =>
    navigate({ pathname: "/complaint/complaint-summary", search });

  const questions = problemOat?.questions ?? [];
  const hasQuestions = questions.length > 0;
  const total = questions.length;
  const currentIndexClamped = total ? Math.min(currentIndex, total - 1) : 0;
  useEffect(() => {
    if (total && currentIndex > total - 1) setCurrentIndex(total - 1);
  }, [total, currentIndex]);
  useEffect(() => {
    document
      .querySelector<HTMLElement>(".wizard-page__content")
      ?.scrollTo({ top: 0, left: 0 });
  }, [currentIndexClamped]);
  const completedCount = questions.filter((q) => (q.answer ?? "").trim().length > 0).length;
  const currentQuestion = questions[currentIndexClamped];
  const currentQuestionAnswered = (currentQuestion?.answer ?? "").trim().length > 0;
  const isFirst = currentIndexClamped === 0;
  const isLast = currentIndexClamped === total - 1;

  if (!complaintId || (!patientID && !caseHistoryId)) {
    return (
      <div className="wizard-page w-full">
        <div className="wizard-page__content p-4">
          <p className="text-sm text-slate-600">
            Missing complaint or patient. Go back to complaints.
          </p>
        </div>
        <WizardActionsBar>
          <Button type="button" variant="outline" onClick={() => navigate("/complaint/primary-complaint")}>
            {t.backButton}
          </Button>
        </WizardActionsBar>
      </div>
    );
  }

  const goPrev = () => {
    if (isFirst) goBackWizard();
    else setCurrentIndex((i) => Math.max(0, i - 1));
  };

  const goNext = () => {
    if (isLast) saveAndContinue();
    else setCurrentIndex((i) => Math.min(total - 1, i + 1));
  };

  return (
    <div className="wizard-page w-full">
      <div className="wizard-page__content min-w-0 p-4 flex flex-col">
        <div className="outcome-assessment-card mx-auto w-full max-w-2xl min-w-0 flex-1 min-h-0 flex flex-col">
          <>
          <ComplaintStepHeader complaintId={complaintId} step="outcome" heading="Outcome Assessment" />

          {isLoading && (
            <div className="wizard-skeleton" role="status" aria-busy="true" aria-label="Loading outcome assessment">
              <span />
              <span />
              <span />
            </div>
          )}

          {!isLoading && loadError && (
            <StepLoadError
              message="We couldn't load these questions. Nothing has been skipped."
              onRetry={() => void loadOats()}
            />
          )}

          {!isLoading && !loadError && !hasQuestions && (
            <>
            <p className="text-sm text-healthsource-gray mt-4">
              No outcome assessment questions for this complaint.
            </p>
            </>
          )}

          {!isLoading && hasQuestions && problemOat && currentQuestion && (
            <>
              <p className="outcome-assessment-progress-text">
                {completedCount} of {total} completed
              </p>

              {problemOat.prompt && currentIndexClamped === 0 && (
                <p className="outcome-assessment-prompt">{problemOat.prompt}</p>
              )}

              <div className="complaint-history-section-block flex-1 min-h-0 flex flex-col">
                <p className="outcome-assessment-question" id="outcome-question">
                  {currentQuestion.question}
                </p>
                {currentQuestion.answers && currentQuestion.answers.length > 0 ? (
                  <div className="outcome-assessment-answers" role="group" aria-labelledby="outcome-question">
                    {currentQuestion.answers.map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        disabled={savingIndex === currentIndexClamped}
                        aria-pressed={currentQuestion.answer === opt}
                        onClick={() => handleAnswer(problemOat, currentIndexClamped, opt)}
                        className={cn(
                          "outcome-assessment-answer-btn",
                          currentQuestion.answer === opt && "outcome-assessment-answer-btn--selected"
                        )}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                ) : (
                  <input
                    type="text"
                    className="w-full rounded-lg border border-healthsource-lightLavender px-4 py-3 text-base text-healthsource-dark focus:outline-none focus:ring-2 focus:ring-healthsource-navy/40 md:text-sm"
                    aria-labelledby="outcome-question"
                    placeholder="Your answer"
                    value={currentQuestion.answer}
                    onChange={(e) => {
                      const value = e.target.value;
                      setProblemOat({
                        ...problemOat,
                        questions: problemOat.questions.map((qu, i) =>
                          i === currentIndexClamped ? { ...qu, answer: value } : qu
                        ),
                      });
                    }}
                    onBlur={() => {
                      const oat = usePrimaryComplaintStore.getState().problemOat;
                      if (oat?.questions[currentIndexClamped]?.answer?.trim()) {
                        setSavingIndex(currentIndexClamped);
                        updateProblemOat(complaintId, oat, currentIndexClamped)
                          .then(() => {
                            if (currentIndexClamped === total - 1) saveAndContinue();
                            else setCurrentIndex((i) => Math.min(total - 1, i + 1));
                          })
                          .catch(() => {})
                          .finally(() => setSavingIndex(null));
                      }
                    }}
                  />
                )}
                {missing && !currentQuestionAnswered && (
                  <p className="choice-group__error" role="alert">
                    {t.answerToContinue}
                  </p>
                )}
              </div>
            </>
          )}
          </>
        </div>

      </div>
      {!isLoading && hasQuestions && (
        <WizardActionsBar>
          <Button type="button" variant="outline" onClick={goPrev}>
            ← {t.backButton}
          </Button>
          <Button
            type="button"
            onClick={currentQuestionAnswered ? goNext : () => setMissing(true)}
          >
            {isLast ? t.continueButton : "Next"}
          </Button>
        </WizardActionsBar>
      )}

      {!isLoading && loadError && (
        <WizardActionsBar>
          <Button type="button" variant="outline" onClick={goBackWizard}>
            ← {t.backButton}
          </Button>
        </WizardActionsBar>
      )}

      {!isLoading && !loadError && !hasQuestions && (
        <WizardActionsBar>
          <Button type="button" variant="outline" onClick={goBackWizard}>
            {t.backButton}
          </Button>
        </WizardActionsBar>
      )}
    </div>
  );
}
